import { useMutation } from '@apollo/client';
import type { InsertDocumentVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Document_File_Type_Enum,
  InsertDocumentVersionDocument,
  namedOperations,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertDocumentVersionTRPCInput } from './useInsertDocumentVersionTRPC';
import { useInsertDocumentVersionTRPC } from './useInsertDocumentVersionTRPC';

interface InsertDocumentVersionGraphQLVariables {
  ParentDocumentId: string;
  Type: Document_File_Type_Enum;
  FileId: string | null;
  Content: string | null;
  Link: string | null;
  Version: InsertDocumentVersionTRPCInput['Version'];
  Summary: InsertDocumentVersionTRPCInput['Summary'];
  ReasonForReview: InsertDocumentVersionTRPCInput['ReasonForReview'];
  ReviewedBy: InsertDocumentVersionTRPCInput['ReviewedBy'];
  ReviewDate: InsertDocumentVersionTRPCInput['ReviewDate'];
  NextReviewDate: InsertDocumentVersionTRPCInput['NextReviewDate'];
  CustomAttributeData: InsertDocumentVersionTRPCInput['CustomAttributeData'];
}

const toGraphQLVariables = (
  input: InsertDocumentVersionTRPCInput
): InsertDocumentVersionGraphQLVariables => {
  const shared = {
    ParentDocumentId: input.ParentDocumentId,
    Version: input.Version,
    Summary: input.Summary,
    ReasonForReview: input.ReasonForReview,
    ReviewedBy: input.ReviewedBy,
    ReviewDate: input.ReviewDate,
    NextReviewDate: input.NextReviewDate,
    CustomAttributeData: input.CustomAttributeData,
  };
  switch (input.Type) {
    case 'file':
      return {
        ...shared,
        Type: Document_File_Type_Enum.File,
        FileId: input.FileId,
        Content: null,
        Link: null,
      };
    case 'html':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Html,
        FileId: null,
        Content: input.Content,
        Link: null,
      };
    case 'link':
      return {
        ...shared,
        Type: Document_File_Type_Enum.Link,
        FileId: null,
        Content: null,
        Link: input.Link,
      };
  }
};

export const useInsertDocumentVersion = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertDocumentVersionDocument,
    {
      update: (cache) => {
        evictField(cache, 'document');
      },
      refetchQueries: [namedOperations.Query.getDocumentFileById],
    }
  );

  const trpcMutation = useInsertDocumentVersionTRPC();

  const insertDocumentVersion = async (
    input: InsertDocumentVersionTRPCInput
  ): Promise<InsertDocumentVersionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertDocumentVersion(input);
    }

    const result = await insertGraphQL({
      variables: toGraphQLVariables(input),
    });
    if (!result.data) {
      throw new Error('Failed to insert document version');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertDocumentVersion,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertDocumentVersion,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
